import React, { useEffect, useState } from 'react';
import { useEditor, PAGE_PRESETS, activeMasterOf } from '../../context/EditorContext';
import { WpDialog, Field, NumberInput, SelectInput, CheckInput } from './DialogShell';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { useInsert } from '../../hooks/useInsert';
import { getBusinessInfo, saveBusinessInfo, apiErrorMessage } from '../../lib/projectApi';
import { pickLocalFile } from '../editor/ObjectContextMenu';
import { getBaselineGuide, BASELINE_GUIDE_ID } from '../../lib/textLayout';
import { toast } from 'sonner';

/** Hyperlink dialog. props: { objectId } */
export const HyperlinkDialog = ({ props }) => {
  const { closeDialog, dispatch, getObjectById } = useEditor();
  const obj = getObjectById(props.objectId);
  const [url, setUrl] = useState(obj?.properties?.hyperlink || '');
  const ok = () => { if (obj) dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [obj.id], props: { hyperlink: url.trim() } } }); closeDialog(); };
  return (
    <WpDialog name="hyperlink" title="Insert Hyperlink" width="w-[480px]" onOk={ok} onCancel={closeDialog} extraFooter={obj?.properties?.hyperlink ? <Button variant="ghost" className="mr-auto" onClick={() => { dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [obj.id], props: { hyperlink: '' } } }); closeDialog(); }} data-testid="hyperlink-remove">Remove Link</Button> : null}>
      <Field label="Address" hint="Links are included when you export to PDF."><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" autoFocus data-testid="hyperlink-url" /></Field>
    </WpDialog>
  );
};

/** Insert Table dialog */
export const InsertTableDialog = () => {
  const { closeDialog } = useEditor();
  const ins = useInsert();
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  return (
    <WpDialog name="insert-table" title="Create Table" width="w-[400px]" onOk={() => { ins.insertTable(Math.round(rows), Math.round(cols)); closeDialog(); }} onCancel={closeDialog}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Number of rows"><NumberInput value={rows} onChange={setRows} min={1} max={60} testId="insert-table-rows" /></Field>
        <Field label="Number of columns"><NumberInput value={cols} onChange={setCols} min={1} max={20} testId="insert-table-cols" /></Field>
      </div>
    </WpDialog>
  );
};

/** Insert Page dialog */
export const InsertPageDialog = () => {
  const { closeDialog, dispatch, state } = useEditor();
  const [count, setCount] = useState(1);
  const [where, setWhere] = useState('after');
  const [dup, setDup] = useState(false);
  const ok = () => {
    const base = where === 'after' ? state.currentPageIndex + 1 : state.currentPageIndex;
    for (let i = 0; i < Math.round(count); i++) dispatch({ type: 'ADD_PAGE', payload: { index: base + i, duplicate: dup } });
    toast.success(`${Math.round(count)} page(s) inserted`);
    closeDialog();
  };
  return (
    <WpDialog name="insert-page" title="Insert Page" width="w-[420px]" onOk={ok} onCancel={closeDialog}>
      <div className="space-y-3">
        <Field label="Number of new pages"><NumberInput value={count} onChange={setCount} min={1} max={50} testId="insert-page-count" /></Field>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="radio" checked={where === 'before'} onChange={() => setWhere('before')} className="accent-[hsl(var(--primary))]" data-testid="insert-page-before" /> Before current page</label>
          <label className="flex items-center gap-2 text-sm"><input type="radio" checked={where === 'after'} onChange={() => setWhere('after')} className="accent-[hsl(var(--primary))]" data-testid="insert-page-after" /> After current page</label>
        </div>
        <CheckInput checked={dup} onChange={setDup} label="Duplicate all objects on the current page" testId="insert-page-duplicate" />
      </div>
    </WpDialog>
  );
};

/** Rename page dialog. props: { index } */
export const RenamePageDialog = ({ props }) => {
  const { closeDialog, dispatch, state } = useEditor();
  const page = state.pages[props.index ?? state.currentPageIndex];
  const [name, setName] = useState(page?.name || '');
  const ok = () => { if (name.trim()) dispatch({ type: 'UPDATE_PAGE', payload: { index: props.index ?? state.currentPageIndex, data: { name: name.trim() } } }); closeDialog(); };
  return (
    <WpDialog name="rename-page" title="Rename Page" width="w-[400px]" onOk={ok} onCancel={closeDialog}>
      <Field label="Page name"><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && ok()} data-testid="rename-page-input" /></Field>
    </WpDialog>
  );
};

/** Rename a master page. props: { id } */
export const RenameMasterDialog = ({ props }) => {
  const { closeDialog, dispatch, state } = useEditor();
  const master = state.masters[props.id] || activeMasterOf(state);
  const [name, setName] = useState(master?.name || '');
  const ok = () => { if (master && name.trim()) dispatch({ type: 'RENAME_MASTER', payload: { id: master.id, name: name.trim() } }); closeDialog(); };
  return (
    <WpDialog name="rename-master" title="Rename Master Page" width="w-[400px]" onOk={ok} onCancel={closeDialog}>
      <Field label="Master page name" hint="Shown in the Pages pane and the Master Page tab"><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && ok()} data-testid="rename-master-input" /></Field>
    </WpDialog>
  );
};

/** Page Setup: put the caret in the Width field once the dialog is mounted (pure DOM helper, no component inputs). */
const focusWidth = () => window.setTimeout(() => { const el = document.querySelector('[data-testid="page-setup-width"]'); if (el) { el.focus(); el.select?.(); } }, 60);

/** Page Setup dialog */
export const PageSetupDialog = ({ props = {} }) => {
  const { closeDialog, dispatch, state, getCurrentPage, pxToUnit, unitToPx } = useEditor();
  const page = getCurrentPage();
  const [w, setW] = useState(page.width);
  const [h, setH] = useState(page.height);
  const [all, setAll] = useState(true);
  const u = state.canvasUnit;
  // Opened from a "Custom" page-size choice: put the cursor in the Width field so the user can type a size straight away
  useEffect(() => { if (props.focus === 'width') focusWidth(); }, [props.focus]);
  const presetKey = Object.keys(PAGE_PRESETS).find((k) => (PAGE_PRESETS[k].width === w && PAGE_PRESETS[k].height === h) || (PAGE_PRESETS[k].width === h && PAGE_PRESETS[k].height === w)) || 'custom';
  const ok = () => {
    if (all) dispatch({ type: 'UPDATE_ALL_PAGES_SIZE', payload: { width: Math.round(w), height: Math.round(h) } });
    else dispatch({ type: 'UPDATE_PAGE', payload: { index: state.currentPageIndex, data: { width: Math.round(w), height: Math.round(h) } } });
    toast.success('Page size updated');
    closeDialog();
  };
  return (
    <WpDialog name="page-setup" title="Page Setup" width="w-[520px]" onOk={ok} onCancel={closeDialog}>
      <div className="grid grid-cols-[1fr_140px] gap-5">
        <div className="space-y-3">
          <Field label="Page size"><SelectInput value={presetKey} onChange={(k) => { if (k === 'custom') { focusWidth(); return; } const p = PAGE_PRESETS[k]; setW(w > h ? Math.max(p.width, p.height) : Math.min(p.width, p.height)); setH(w > h ? Math.min(p.width, p.height) : Math.max(p.width, p.height)); }} options={[...Object.entries(PAGE_PRESETS).map(([k, p]) => ({ value: k, label: p.name })), { value: 'custom', label: 'Custom (type a width and height)' }]} className="w-full" testId="page-setup-preset" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Width (${u})`}><NumberInput value={Number(pxToUnit(w))} onChange={(v) => setW(Math.max(48, unitToPx(v)))} step={0.1} testId="page-setup-width" /></Field>
            <Field label={`Height (${u})`}><NumberInput value={Number(pxToUnit(h))} onChange={(v) => setH(Math.max(48, unitToPx(v)))} step={0.1} testId="page-setup-height" /></Field>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant={w <= h ? 'default' : 'outline'} size="sm" onClick={() => { if (w > h) { setW(h); setH(w); } }} data-testid="page-setup-portrait">Portrait</Button>
            <Button type="button" variant={w > h ? 'default' : 'outline'} size="sm" onClick={() => { if (w <= h) { setW(h); setH(w); } }} data-testid="page-setup-landscape">Landscape</Button>
          </div>
          <CheckInput checked={all} onChange={setAll} label="Apply to all pages" testId="page-setup-all-pages" />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Preview</div>
          <div className="h-[150px] rounded-md border border-border bg-muted/40 flex items-center justify-center"><div className="bg-white border border-border shadow-sm" style={{ width: w >= h ? 100 : (100 * w) / h, height: w >= h ? (100 * h) / w : 100 }} data-testid="page-setup-preview" /></div>
          <div className="text-[11px] text-muted-foreground mt-1 text-center">{Math.round(w)} × {Math.round(h)} px</div>
        </div>
      </div>
    </WpDialog>
  );
};

/** Layout Guides dialog: margin guides + grid guides (columns/rows) + baseline guides. props: { tab?: 'margin'|'grid'|'baseline' } */
export const LayoutGuidesDialog = ({ props = {} }) => {
  const { closeDialog, rawDispatch, state, getCurrentPage, pxToUnit, unitToPx } = useEditor();
  const page = getCurrentPage();
  const existing = (side) => state.guides.find((g) => g.id === `m-${side}`);
  const [tab, setTab] = useState(props.tab || 'margin');
  const [m, setM] = useState({ left: existing('left')?.position ?? 48, right: existing('right') ? page.width - existing('right').position : 48, top: existing('top')?.position ?? 48, bottom: existing('bottom') ? page.height - existing('bottom').position : 48 });
  const [cols, setCols] = useState(1);
  const [rows, setRows] = useState(1);
  const [gap, setGap] = useState(19);
  // Baseline guides are saved with the publication as a special guide entry { id: 'baseline', kind: 'baseline', spacing, offset }
  const bg = getBaselineGuide(state.guides, state.viewOptions?.baselineSpacing);
  const [baseline, setBaseline] = useState(bg.spacing);
  const [offset, setOffset] = useState(bg.offset);
  const u = state.canvasUnit;
  const ok = () => {
    // keep user guides (incl. fold lines); margin / grid / baseline guides are regenerated below
    const keep = state.guides.filter((g) => !g.kind || g.kind === 'fold');
    const guides = [
      { id: 'm-left', orientation: 'vertical', position: m.left, kind: 'margin' }, { id: 'm-right', orientation: 'vertical', position: page.width - m.right, kind: 'margin' },
      { id: 'm-top', orientation: 'horizontal', position: m.top, kind: 'margin' }, { id: 'm-bottom', orientation: 'horizontal', position: page.height - m.bottom, kind: 'margin' },
    ];
    const innerW = page.width - m.left - m.right, innerH = page.height - m.top - m.bottom;
    for (let c = 1; c < cols; c++) { const x = m.left + (c * (innerW + gap)) / cols; guides.push({ id: `c-${c}-a`, orientation: 'vertical', position: x - gap, kind: 'grid' }, { id: `c-${c}-b`, orientation: 'vertical', position: x, kind: 'grid' }); }
    for (let r = 1; r < rows; r++) { const y = m.top + (r * (innerH + gap)) / rows; guides.push({ id: `r-${r}-a`, orientation: 'horizontal', position: y - gap, kind: 'grid' }, { id: `r-${r}-b`, orientation: 'horizontal', position: y, kind: 'grid' }); }
    const spacing = Math.max(2, Math.round(Number(baseline) || 14));
    guides.push({ id: BASELINE_GUIDE_ID, kind: 'baseline', spacing, offset: Math.max(0, Math.round(Number(offset) || 0)) });
    rawDispatch({ type: 'CLEAR_GUIDES' });
    [...keep, ...guides].forEach((g) => rawDispatch({ type: 'ADD_GUIDE', payload: g }));
    rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'baselineSpacing', value: spacing } });
    toast.success('Layout guides updated');
    closeDialog();
  };
  const MI = ({ k }) => <NumberInput value={Number(pxToUnit(m[k]))} onChange={(v) => setM((s) => ({ ...s, [k]: Math.max(0, unitToPx(v)) }))} step={0.05} testId={`guides-margin-${k}`} />;
  const tabBtn = (v, l) => <button key={v} type="button" onClick={() => setTab(v)} className={`flex-1 h-7 rounded-sm text-xs ${tab === v ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`dialog-layout-guides-tab-${v}`}>{l}</button>;
  const previewLines = Math.max(1, Math.floor((page.height - offset) / Math.max(2, baseline)));
  return (
    <WpDialog name="layout-guides" title="Layout Guides" width="w-[560px]" onOk={ok} onCancel={closeDialog}>
      <div className="flex gap-1 mb-3 bg-muted p-1 rounded-md" role="tablist">{tabBtn('margin', 'Margin Guides')}{tabBtn('grid', 'Grid Guides')}{tabBtn('baseline', 'Baseline Guides')}</div>
      <div className="grid grid-cols-[1fr_150px] gap-5">
        <div className="space-y-4">
          {tab === 'margin' && (
            <fieldset className="rounded-md border border-border p-3"><legend className="px-1 text-xs text-muted-foreground">Margin guides ({u})</legend>
              <div className="grid grid-cols-2 gap-3"><Field label="Left"><MI k="left" /></Field><Field label="Right"><MI k="right" /></Field><Field label="Top"><MI k="top" /></Field><Field label="Bottom"><MI k="bottom" /></Field></div>
            </fieldset>
          )}
          {tab === 'grid' && (
            <fieldset className="rounded-md border border-border p-3"><legend className="px-1 text-xs text-muted-foreground">Grid guides</legend>
              <div className="grid grid-cols-3 gap-3"><Field label="Columns"><NumberInput value={cols} onChange={(v) => setCols(Math.round(v))} min={1} max={12} testId="guides-columns" /></Field><Field label="Rows"><NumberInput value={rows} onChange={(v) => setRows(Math.round(v))} min={1} max={12} testId="guides-rows" /></Field><Field label="Spacing (px)"><NumberInput value={gap} onChange={setGap} min={0} max={200} testId="guides-gap" /></Field></div>
            </fieldset>
          )}
          {tab === 'baseline' && (
            <fieldset className="rounded-md border border-border p-3 space-y-3" data-testid="guides-baseline-panel"><legend className="px-1 text-xs text-muted-foreground">Horizontal baseline</legend>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Spacing (px)" hint={`${(baseline * 0.75).toFixed(1)} pt`}><NumberInput value={baseline} onChange={setBaseline} min={2} max={200} testId="guides-baseline" /></Field>
                <Field label="Offset (px)" hint={`${(offset * 0.75).toFixed(1)} pt`}><NumberInput value={offset} onChange={setOffset} min={0} max={400} testId="guides-baseline-offset" /></Field>
              </div>
              <CheckInput checked={!!state.viewOptions?.baselines} onChange={(v) => rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'baselines', value: v } })} label="Show baseline guides (View ▸ Baselines)" testId="guides-show-baselines" />
              <p className="text-[11px] text-muted-foreground">Paragraphs with <b>Align to Baselines</b> (Home ▸ Line Spacing, or the Paragraph dialog) snap each line to this grid so text in neighbouring columns lines up.</p>
            </fieldset>
          )}
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Preview</div>
          <div className="relative bg-white border border-border shadow-sm mx-auto overflow-hidden" style={{ width: 100, height: (100 * page.height) / page.width }} data-testid="guides-preview">
            {tab === 'baseline' && (
              <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(to bottom, rgba(56,189,248,0.7) 1px, transparent 1px)', backgroundSize: `100% ${Math.max(1.5, (100 * Math.max(2, baseline)) / page.width)}px`, backgroundPosition: `0 ${(100 * offset) / page.width}px` }} data-testid="guides-baseline-preview" data-lines={previewLines} />
            )}
            <div className="absolute border border-dashed border-sky-500" style={{ left: `${(m.left / page.width) * 100}%`, right: `${(m.right / page.width) * 100}%`, top: `${(m.top / page.height) * 100}%`, bottom: `${(m.bottom / page.height) * 100}%`, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)`, gap: 2 }}>
              {Array.from({ length: cols * rows }).map((_, i) => <div key={i} className={tab === 'baseline' ? '' : 'bg-sky-100/60'} />)}
            </div>
          </div>
        </div>
      </div>
    </WpDialog>
  );
};

/** Business Information dialog */
export const BusinessInfoDialog = () => {
  const { closeDialog, rawDispatch, state } = useEditor();
  const [info, setInfo] = useState(state.businessInfo || null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!info) getBusinessInfo().then(setInfo).catch(() => setInfo({})); }, [info]);
  const set = (k, v) => setInfo((i) => ({ ...(i || {}), [k]: v }));
  const ok = async () => {
    setBusy(true);
    try { const saved = await saveBusinessInfo({ ...(info || {}), id: 'default' }); rawDispatch({ type: 'SET_BUSINESS_INFO', payload: saved }); toast.success('Business information saved'); closeDialog(); }
    catch (e) { toast.error(await apiErrorMessage(e, 'Could not save.')); } finally { setBusy(false); }
  };
  const fields = [['name', 'Individual name'], ['title', 'Job position or title'], ['organization', 'Organization name'], ['phone', 'Phone'], ['fax', 'Fax'], ['email', 'Email'], ['website', 'Website'], ['tagline', 'Tagline or motto']];
  return (
    <WpDialog name="business-info" title="Business Information" width="w-[620px]" onOk={ok} okLabel={busy ? 'Saving…' : 'Save'} okDisabled={busy || !info} onCancel={closeDialog}>
      {!info ? <div className="text-muted-foreground">Loading…</div> : (
        <div className="grid grid-cols-2 gap-3">
          {fields.map(([k, l]) => <Field key={k} label={l}><Input value={info[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`business-info-${k}`} /></Field>)}
          <Field label="Address" className="col-span-2"><Textarea rows={2} value={info.address || ''} onChange={(e) => set('address', e.target.value)} data-testid="business-info-address" /></Field>
          <Field label="Logo" className="col-span-2">
            <div className="flex items-center gap-3">
              <div className="h-14 w-20 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden">{info.logo ? <img src={info.logo} alt="Logo" className="max-h-full max-w-full object-contain" /> : <span className="text-[11px] text-muted-foreground">No logo</span>}</div>
              <Button type="button" variant="outline" size="sm" onClick={() => pickLocalFile((src) => set('logo', src))} data-testid="business-info-logo-upload">Choose logo…</Button>
              {info.logo && <Button type="button" variant="ghost" size="sm" onClick={() => set('logo', null)}>Remove</Button>}
            </div>
          </Field>
        </div>
      )}
    </WpDialog>
  );
};
